// src/hooks/useLessonEditor.js
import { useState, useCallback, useEffect, useContext } from "react";
import { CourseContext } from "../context/CourseContext";
import { useAxiosAuth } from "./useAxiosAuth";
import { parseHTML, buildFullHTML } from "../utils/htmlUtils";

export function useLessonEditor() {
  const axios = useAxiosAuth();

  // ---------- shared page state ----------
  const [courses, setCourses] = useState([]);
  const [lessons, setLessons] = useState([]);
  const {
    selectedCourse,
    setSelectedCourse,
    selectedLesson,
    setSelectedLesson,
  } = useContext(CourseContext);
  const [baseUrl, setBaseUrl] = useState("");

  const [head, setHead] = useState("");
  const [body, setBody] = useState("");
  const [origHead, setOrigHead] = useState("");
  const [origBody, setOrigBody] = useState("");

  // ---------- data fetch helpers ----------
  const loadCourses = useCallback(async () => {
    try {
      const { data } = await axios.get("/courses/");
      setCourses(data);
    } catch(e) {
      // setCoursesError(e);
      console.error(e)
    }
  }, [axios]);

  const loadLessons = useCallback(
    async (courseId) => {
      const { data } = await axios.get(`/courses/${courseId}`);
      setLessons(data.lessons);
    },
    [axios]
  );

  const loadContent = useCallback(
    async (lessonId) => {
      const { data } = await axios.get(`/lessons/${lessonId}`);
      const { head, body } = parseHTML(data.content);
      setHead(head);
      setBody(body);
      setOrigHead(head);
      setOrigBody(body);
    },
    [axios]
  );

  // in useLessonEditor.js
  const saveBody = useCallback(
    async (rawHtmlString) => {
      // 1) push new markup to preview immediately
      setBody(rawHtmlString);
  
      // 2) persist to server
      await axios.put(`/lessons/${selectedLesson}`, {
        content: buildFullHTML(head, rawHtmlString),
      });
  
      // 3) mark as clean
      setOrigBody(rawHtmlString);
    },
    [axios, selectedLesson, head]       // deps that appear inside
  );

  // ---------- effects ----------
  useEffect(() => {
    loadCourses();
  }, [loadCourses]);

  useEffect(() => {
    if (selectedCourse) loadLessons(selectedCourse);
  }, [selectedCourse, loadLessons]);

  useEffect(() => {
    if (selectedLesson) loadContent(selectedLesson);
  }, [selectedLesson, loadContent]);

  // keep preview baseUrl in sync
  useEffect(() => {
    if (!selectedLesson) return;
    const lessonObj = lessons.find(
      (l) => l.id.toString() === selectedLesson.toString()
    );
    if (lessonObj?.content_gcp_path) {
      const folder = lessonObj.content_gcp_path.replace(/\/[^/]*$/, "/");
      //const folder = lessonObj.content_gcp_path;
      setBaseUrl(folder);
    } else {
      setBaseUrl("");
    }
  }, [lessons, selectedLesson]);

  return {
    // exposed state
    courses,
    lessons,
    selectedCourse,
    selectedLesson,
    setSelectedCourse,
    setSelectedLesson,
    head,
    setHead,
    body,
    setBody,
    baseUrl,
    isDirty: head !== origHead || body !== origBody,
    // actions
    saveBody,
  };
}
